/**
 * ════════════════════════════════════════════════════════════════
 * OfferSection — Éditeur de Conditions d'achats (Promotional Mechanics)
 * ════════════════════════════════════════════════════════════════
 *
 * ARCHITECTURE: Ce composant fait partie du moteur Conditions d'achats.
 * Il édite les promotional_mechanics[] des distributions.
 *
 * ⚠️ NAMING: Le nom "OfferSection" est historique et trompeur.
 * Ce composant édite les Conditions d'achats, PAS les Offres marketing.
 * Futur renommage prévu: OfferSection → ConditionsAchatEditor
 *
 * Voir: OFFERS_ARCHITECTURE.md §1
 * ════════════════════════════════════════════════════════════════
 */

import React, { useState, useImperativeHandle, forwardRef, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2, ChevronDown, ChevronRight, Tag, Calendar, Layers, Zap, AlertCircle } from 'lucide-react';

const NATURE_OPTS = [
  { value: 'PERMANENTE', label: 'Permanente' }, { value: 'PRECOMMANDE', label: 'Pr\u00e9commande' },
  { value: 'LANCEMENT', label: 'Lancement' }, { value: 'PROMOTION', label: 'Promotion' },
  { value: 'SOLDES', label: 'Soldes' }, { value: 'PROMO_FLASH', label: 'Promo flash' },
  { value: 'DESTOCKAGE', label: 'D\u00e9stockage' }, { value: 'EVENEMENTIELLE', label: '\u00c9v\u00e9nementielle' }
];
const EFFECT_OPTS = [
  { value: 'FREE_UNITS', label: 'Gratuit\u00e9 (unit\u00e9s offertes)' },
  { value: 'DISCOUNT_PERCENT', label: 'Remise (%)' },
  { value: 'UNIT_PRICE_OVERRIDE', label: 'Prix unitaire fixe (\u20ac)' }
];
const NATURE_COLORS = {
  PERMANENTE: 'border-l-emerald-500', PRECOMMANDE: 'border-l-blue-500', LANCEMENT: 'border-l-violet-500',
  PROMOTION: 'border-l-red-500', SOLDES: 'border-l-amber-500', PROMO_FLASH: 'border-l-orange-500',
  DESTOCKAGE: 'border-l-yellow-500', EVENEMENTIELLE: 'border-l-pink-500'
};
const STATUS_COLORS = {
  PLANIFIED: 'bg-blue-100 text-blue-700', ACTIVE: 'bg-green-100 text-green-800', ARCHIVED: 'bg-gray-200 text-gray-500'
};
const STATUS_LABELS = { PLANIFIED: 'Planifiée', ACTIVE: 'Active', ARCHIVED: 'Archivée' };

// Compute status from dates (unified, matches backend compute_mechanic_status)
function computeStatus(mech) {
  const now = new Date();
  const dStart = mech.date_debut;
  const dEnd = mech.date_fin;
  if (!dStart && !dEnd) return 'ACTIVE';
  // Parse start: simple date → midnight UTC, ISO datetime → as-is
  if (dStart) {
    const s = String(dStart);
    const startDt = s.includes('T') ? new Date(s.replace('Z', '+00:00')) : new Date(s + 'T00:00:00+00:00');
    if (!isNaN(startDt) && now < startDt) return 'PLANIFIED';
  }
  // Parse end: simple date → 23:59:59 UTC, ISO datetime → as-is
  if (dEnd) {
    const e = String(dEnd);
    const endDt = e.includes('T') ? new Date(e.replace('Z', '+00:00')) : new Date(e + 'T23:59:59+00:00');
    if (!isNaN(endDt) && now > endDt) return 'ARCHIVED';
  }
  return 'ACTIVE';
}

// Palier value label per effect_type
const VALUE_LABELS = { FREE_UNITS: 'Unités offertes', DISCOUNT_PERCENT: '% de remise', UNIT_PRICE_OVERRIDE: 'Prix unitaire (€)' };
const VALUE_STEPS = { FREE_UNITS: '1', DISCOUNT_PERCENT: '0.1', UNIT_PRICE_OVERRIDE: '0.01' };
const VALUE_MIN = { FREE_UNITS: '1', DISCOUNT_PERCENT: '0.1', UNIT_PRICE_OVERRIDE: '0.01' };

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : '';

/**
 * OfferSection V4 — Admin/Partner mechanic editor
 * - Status auto-computed (read-only)
 * - Validation DEFERRED to SAVE only — no errors during input
 * - Per-field error highlighting (red border) after failed save
 * - Exposes validate() via ref for parent form submit
 */
const OfferSection = forwardRef(({ mechanics = [], onChange, disabled = false, userRole = 'admin' }, ref) => {
  const safeMechanics = Array.isArray(mechanics) ? mechanics : [];
  const [expandedIdx, setExpandedIdx] = useState(safeMechanics.length > 0 ? 0 : -1);
  const [validationErrors, setValidationErrors] = useState({}); // {mechIdx: [error strings]}
  const [fieldErrors, setFieldErrors] = useState({}); // {mechIdx: Set of field names}
  const isRetailer = userRole === 'retailer';

  // Validation function — called at submit time only
  const validate = useCallback(() => {
    const errors = {};
    const fields = {};
    let hasError = false;
    safeMechanics.forEach((m, i) => {
      const mechErrors = [];
      const badFields = new Set();
      const title = (m.title || '').trim();
      if (!title) { mechErrors.push('Le titre est obligatoire'); badFields.add('title'); }
      const nat = m.nature_offre || '';
      if (!nat) { mechErrors.push('La nature de l\u2019offre est obligatoire'); badFields.add('nature_offre'); }
      const et = m.effect_type || '';
      if (!et) { mechErrors.push('La m\u00e9canique promotionnelle est obligatoire'); badFields.add('effect_type'); }
      if (!m.date_debut) { mechErrors.push('La date de d\u00e9but est obligatoire'); badFields.add('date_debut'); }
      if (!m.date_fin && nat && nat !== 'PERMANENTE') { mechErrors.push('La date de fin est obligatoire (sauf PERMANENTE)'); badFields.add('date_fin'); }
      if (m.date_debut && m.date_fin && m.date_debut > m.date_fin) { mechErrors.push('Date d\u00e9but doit \u00eatre \u2264 date fin'); badFields.add('date_debut'); badFields.add('date_fin'); }
      const rules = m.pricing_rules || [];
      if (rules.length === 0) { mechErrors.push('Au moins 1 palier est requis'); badFields.add('pricing_rules'); }
      rules.forEach((r, j) => {
        if (!r.min_quantity || r.min_quantity < 1) { mechErrors.push(`Palier ${j + 1}: quantit\u00e9 min >= 1`); badFields.add(`palier_min_${j}`); }
        if (!r.effect_value || r.effect_value <= 0) { mechErrors.push(`Palier ${j + 1}: valeur > 0`); badFields.add(`palier_val_${j}`); }
      });
      if (mechErrors.length > 0) {
        errors[i] = mechErrors;
        fields[i] = badFields;
        hasError = true;
      }
    });
    setValidationErrors(errors);
    setFieldErrors(fields);
    // Auto-expand first errored mechanic
    if (hasError) {
      const firstErrIdx = parseInt(Object.keys(errors)[0]);
      if (!isNaN(firstErrIdx)) setExpandedIdx(firstErrIdx);
    }
    return !hasError;
  }, [safeMechanics]);

  // Expose validate to parent
  useImperativeHandle(ref, () => ({ validate }), [validate]);

  // Clear errors for a mechanic when edited
  const clearErrors = (idx) => {
    if (validationErrors[idx]) {
      setValidationErrors(prev => { const n = { ...prev }; delete n[idx]; return n; });
    }
    if (fieldErrors[idx]) {
      setFieldErrors(prev => { const n = { ...prev }; delete n[idx]; return n; });
    }
  };

  // Check if a specific field has error
  const hasFieldError = (mechIdx, fieldName) => {
    return fieldErrors[mechIdx]?.has(fieldName) || false;
  };
  const errorBorder = (mechIdx, fieldName) => {
    return hasFieldError(mechIdx, fieldName) ? 'border-red-400 ring-1 ring-red-200' : '';
  };

  const addMechanic = () => {
    const newMech = {
      id: `mech-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      title: '', nature_offre: '',
      date_debut: null, date_fin: null,
      effect_type: '', pricing_rules: []
    };
    const updated = [...safeMechanics, newMech];
    onChange(updated);
    setExpandedIdx(updated.length - 1);
  };

  const updateMech = (idx, field, val) => {
    onChange(safeMechanics.map((m, i) => i === idx ? { ...m, [field]: val } : m));
    clearErrors(idx);
  };

  const removeMech = (idx) => {
    onChange(safeMechanics.filter((_, i) => i !== idx));
    if (expandedIdx === idx) setExpandedIdx(-1);
    else if (expandedIdx > idx) setExpandedIdx(expandedIdx - 1);
  };

  const addPalier = (mechIdx) => {
    const mech = safeMechanics[mechIdx];
    const rules = mech.pricing_rules || [];
    const lastMin = rules.length > 0 ? (rules[rules.length - 1].min_quantity || 1) + 6 : 1;
    const defaultValue = mech.effect_type === 'FREE_UNITS' ? 1 : mech.effect_type === 'DISCOUNT_PERCENT' ? 5 : 8;
    const newRule = {
      id: `pal-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      min_quantity: lastMin, max_quantity: null,
      effect_value: defaultValue, effect_type: mech.effect_type
    };
    updateMech(mechIdx, 'pricing_rules', [...rules, newRule]);
  };

  const updatePalier = (mechIdx, palIdx, field, val) => {
    const rules = [...(safeMechanics[mechIdx].pricing_rules || [])];
    rules[palIdx] = { ...rules[palIdx], [field]: val };
    updateMech(mechIdx, 'pricing_rules', rules);
  };

  const removePalier = (mechIdx, palIdx) => {
    const rules = (safeMechanics[mechIdx].pricing_rules || []).filter((_, i) => i !== palIdx);
    updateMech(mechIdx, 'pricing_rules', rules);
  };

  // Visible mechanics based on role
  const visibleMechanics = isRetailer
    ? safeMechanics.filter(m => computeStatus(m) === 'ACTIVE')
    : safeMechanics;

  return (
    <div data-testid="offer-section">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] font-bold text-indigo-700 uppercase tracking-wider flex items-center gap-1.5">
          <Zap className="h-3.5 w-3.5" /> Conditions d&apos;achat ({visibleMechanics.length})
        </p>
        {!disabled && !isRetailer && (
          <Button type="button" size="sm" variant="outline" onClick={addMechanic}
            className="h-7 text-xs gap-1 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            data-testid="add-mechanic-btn">
            <Plus className="h-3 w-3" /> Mécanique Promotionnelle
          </Button>
        )}
      </div>

      {visibleMechanics.length === 0 && (
        <p className="text-xs text-gray-400 italic py-2">Aucune mécanique promotionnelle.</p>
      )}

      <div className="space-y-2">
        {safeMechanics.map((mech, idx) => {
          const status = computeStatus(mech);
          if (isRetailer && status !== 'ACTIVE') return null;
          const isOpen = expandedIdx === idx;
          const nature = mech.nature_offre || '';
          const rules = mech.pricing_rules || [];
          const effectType = mech.effect_type || '';
          const effectLabel = EFFECT_OPTS.find(e => e.value === effectType)?.label || (effectType || 'Non d\u00e9finie');
          // For decorative purposes only (border color, header badge)
          const natureForColor = nature || 'PROMOTION';
          const natureLabel = NATURE_OPTS.find(n => n.value === nature)?.label || (nature || 'Non d\u00e9finie');

          return (
            <div key={mech.id || idx} className={`border-l-4 ${NATURE_COLORS[natureForColor] || 'border-l-gray-300'} border rounded-lg overflow-hidden bg-white`}
              data-testid={`mechanic-accordion-${idx}`}>
              {/* Header */}
              <div role="button" tabIndex={0}
                onClick={() => setExpandedIdx(isOpen ? -1 : idx)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedIdx(isOpen ? -1 : idx); } }}
                className="w-full flex items-center justify-between px-3 py-2.5 text-left cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
                data-testid={`mechanic-toggle-${idx}`}>
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {isOpen ? <ChevronDown className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" />}
                  <Tag className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                  <span className="text-sm font-semibold text-gray-900 truncate">{mech.title || effectLabel}</span>
                  {/* Status badge — computed, read-only */}
                  <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${STATUS_COLORS[status] || ''}`}>
                    {STATUS_LABELS[status] || status}
                  </span>
                  <span className="text-[10px] text-gray-400">{natureLabel}</span>
                  {nature === 'PERMANENTE' && !mech.date_debut && !mech.date_fin ? (
                    <span className="text-[10px] text-emerald-600">Permanente</span>
                  ) : (mech.date_debut || mech.date_fin) ? (
                    <span className="text-[10px] text-gray-400">{fmtDate(mech.date_debut)}{mech.date_fin ? ` \u2192 ${fmtDate(mech.date_fin)}` : ''}</span>
                  ) : null}
                  {rules.length > 0 && <span className="text-[10px] text-indigo-500">{rules.length} palier{rules.length > 1 ? 's' : ''}</span>}
                </div>
                {!disabled && !isRetailer && (
                  <div className="flex items-center gap-1 flex-shrink-0 ml-2" onClick={(e) => e.stopPropagation()}>
                    <Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400 hover:text-red-600"
                      onClick={() => removeMech(idx)} data-testid={`mechanic-delete-${idx}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Body */}
              {isOpen && (
                <div className="px-3 py-3 space-y-3 border-t border-gray-100">
                  {/* Title */}
                  <div>
                    <Label className="text-[10px] text-gray-500 mb-0.5 block">Titre <span className="text-red-400">*</span></Label>
                    <Input value={mech.title || ''} placeholder="Ex: Remise été 2026"
                      onChange={(e) => updateMech(idx, 'title', e.target.value)}
                      disabled={disabled}
                      className={`h-8 text-sm placeholder:text-gray-300 placeholder:italic placeholder:font-light ${errorBorder(idx, 'title')}`}
                      data-testid={`mechanic-title-${idx}`} />
                  </div>

                  {/* Nature + Effect + Status (read-only) */}
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-[10px] text-gray-500 mb-0.5 block">Nature <span className="text-red-400">*</span></Label>
                      <Select value={nature || undefined} onValueChange={(v) => updateMech(idx, 'nature_offre', v)} disabled={disabled}>
                        <SelectTrigger className={`h-8 text-xs ${errorBorder(idx, 'nature_offre')}`} data-testid={`mechanic-nature-${idx}`}><SelectValue placeholder="&#8212; Choisir une nature &#8212;" /></SelectTrigger>
                        <SelectContent>
                          {NATURE_OPTS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px] text-gray-500 mb-0.5 block">M&eacute;canique <span className="text-red-400">*</span></Label>
                      <Select value={effectType || undefined} onValueChange={(v) => updateMech(idx, 'effect_type', v)} disabled={disabled}>
                        <SelectTrigger className={`h-8 text-xs ${errorBorder(idx, 'effect_type')}`} data-testid={`mechanic-effect-${idx}`}><SelectValue placeholder="&#8212; Choisir une m&#233;canique &#8212;" /></SelectTrigger>
                        <SelectContent>
                          {EFFECT_OPTS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px] text-gray-500 mb-0.5 block">Statut (auto)</Label>
                      <div className={`h-8 flex items-center px-2 rounded-md border text-xs font-medium ${STATUS_COLORS[status] || 'bg-gray-100'}`} data-testid={`mechanic-status-${idx}`}>
                        {STATUS_LABELS[status] || status}
                      </div>
                    </div>
                  </div>

                  {/* Dates — always shown (PERMANENTE can have dates too) */}
                  <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] text-gray-500 mb-0.5 block flex items-center gap-1"><Calendar className="h-3 w-3" /> D&eacute;but <span className="text-red-400">*</span></Label>
                        <Input type="date" value={(mech.date_debut || '').slice(0, 10)}
                          onChange={(e) => updateMech(idx, 'date_debut', e.target.value || null)}
                          disabled={disabled}
                          className={`h-8 text-sm ${errorBorder(idx, 'date_debut')}`}
                          data-testid={`mechanic-date-start-${idx}`} />
                      </div>
                      <div>
                        <Label className="text-[10px] text-gray-500 mb-0.5 block">Fin {nature !== 'PERMANENTE' && <span className="text-red-400">*</span>}{nature === 'PERMANENTE' && <span className="text-gray-400 ml-1">(facultative)</span>}</Label>
                        <Input type="date" value={(mech.date_fin || '').slice(0, 10)}
                          onChange={(e) => updateMech(idx, 'date_fin', e.target.value || null)}
                          disabled={disabled}
                          className={`h-8 text-sm ${errorBorder(idx, 'date_fin')}`}
                          data-testid={`mechanic-date-end-${idx}`} />
                      </div>
                    </div>
                    {nature === 'PERMANENTE' && (
                      <p className="text-[10px] text-emerald-600 italic mt-0.5 flex items-center gap-1" data-testid={`mechanic-permanente-hint-${idx}`}>
                        <Calendar className="h-3 w-3" />
                        Dates facultatives — sans date de fin, l&apos;offre reste active en permanence.
                      </p>
                    )}

                  {/* ═══ PALIERS ═══ */}
                  <div className={`border-t border-gray-100 pt-2 ${hasFieldError(idx, 'pricing_rules') ? 'bg-red-50/30 rounded-lg p-2 border border-red-200' : ''}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <p className={`text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1 ${hasFieldError(idx, 'pricing_rules') ? 'text-red-500' : 'text-gray-500'}`}>
                        <Layers className="h-3 w-3" /> Paliers ({rules.length}) <span className="text-red-400">*</span>
                      </p>
                      {!disabled && (
                        <Button type="button" size="sm" variant="outline" onClick={() => addPalier(idx)}
                          className="h-6 text-[10px] gap-1" data-testid={`add-palier-btn-${idx}`}>
                          <Plus className="h-2.5 w-2.5" /> Palier
                        </Button>
                      )}
                    </div>

                    {rules.length === 0 && <p className={`text-[10px] italic ${hasFieldError(idx, 'pricing_rules') ? 'text-red-400' : 'text-gray-400'}`}>Aucun palier. {hasFieldError(idx, 'pricing_rules') ? '(obligatoire)' : ''}</p>}

                    {rules.map((rule, ri) => (
                      <div key={rule.id || ri} className={`flex items-end gap-1.5 p-2 rounded border mb-1.5 ${
                        hasFieldError(idx, `palier_min_${ri}`) || hasFieldError(idx, `palier_val_${ri}`) ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
                      }`} data-testid={`palier-${idx}-${ri}`}>
                        <div className="w-20">
                          <Label className="text-[9px] text-gray-400 block">D&egrave;s (qt&eacute;)</Label>
                          <Input type="number" min="1" step="1" className={`h-7 text-xs ${errorBorder(idx, `palier_min_${ri}`)}`}
                            value={rule.min_quantity ?? 1}
                            onChange={(e) => updatePalier(idx, ri, 'min_quantity', parseInt(e.target.value) || 1)}
                            disabled={disabled} data-testid={`palier-min-${idx}-${ri}`} />
                        </div>
                        <div className="w-20">
                          <Label className="text-[9px] text-gray-400 block">Max (qt&eacute;)</Label>
                          <Input type="number" min="1" step="1" className="h-7 text-xs" placeholder="--"
                            value={rule.max_quantity ?? ''}
                            onChange={(e) => updatePalier(idx, ri, 'max_quantity', e.target.value === '' ? null : parseInt(e.target.value))}
                            disabled={disabled} data-testid={`palier-max-${idx}-${ri}`} />
                        </div>
                        <div className="w-28">
                          <Label className="text-[9px] text-gray-400 block">{VALUE_LABELS[effectType] || 'Valeur'}</Label>
                          <Input type="number"
                            step={VALUE_STEPS[effectType] || '0.01'}
                            min={VALUE_MIN[effectType] || '0.01'}
                            className={`h-7 text-xs ${errorBorder(idx, `palier_val_${ri}`)}`}
                            value={rule.effect_value ?? ''}
                            onChange={(e) => {
                              let v = parseFloat(e.target.value);
                              if (effectType === 'FREE_UNITS') v = Math.max(1, Math.round(v));
                              updatePalier(idx, ri, 'effect_value', isNaN(v) ? 0 : v);
                            }}
                            disabled={disabled} data-testid={`palier-value-${idx}-${ri}`} />
                        </div>
                        {!disabled && (
                          <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                            onClick={() => removePalier(idx, ri)} data-testid={`palier-delete-${idx}-${ri}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Validation errors (show after submit attempt) */}
                  {validationErrors[idx] && validationErrors[idx].length > 0 && (
                    <div className="mt-2 p-2.5 rounded-lg bg-red-50 border border-red-200" data-testid={`mechanic-errors-${idx}`}>
                      <div className="flex items-center gap-1.5 mb-1">
                        <AlertCircle className="h-3.5 w-3.5 text-red-500" />
                        <span className="text-[10px] font-bold text-red-700 uppercase">Champs manquants</span>
                      </div>
                      {validationErrors[idx].map((err, ei) => (
                        <p key={ei} className="text-[10px] text-red-600 ml-5">&bull; {err}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default OfferSection;
